/**
 * The context the sheet's decoration renderers resolve cells and measure
 * frames through (`SheetElement.decorationsHandle`). Moved out of
 * sheet-element.ts unchanged (r5 FS 2); the element hands in what it read
 * through `this`, read when a callback runs.
 */
import { type Option, RowKey } from "@tento-lona/sheets";
import { SheetViewModel } from "@tento-lona";
import { nonnull } from "@tento-core/assert";
import { Rational } from "@tento-core/rational";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { SheetCellTargets } from "./sheet-cell-targets.ts";
import type { DecorationRendererContext } from "./decorations/decoration-renderer.ts";
import { SheetDelegate } from "./sheet-delegate.ts";
import { SheetBinder } from "./sheet-index-renderer.ts";

/** What the decoration context reads from its sheet element (read when a callback runs). */
export interface DecorationContextHost {
  /** The bound sheet, if any. */
  readonly sheet: Option<SheetViewModel.Surface<SheetBinder>>;
  /** The element's cell lookup. */
  readonly cellTargets: SheetCellTargets;
  /** The element's fixed-child handle. */
  readonly fixedChild: SheetDelegate.FixedChildHandle;
}

/** The decoration renderers' context over `host`. */
export function decorationRendererContext(host: DecorationContextHost): DecorationRendererContext {
  return {
    resolveCell: (position) => host.cellTargets.cell(position),
    resolveCellTargets: (position) => {
      const resolved = host.cellTargets.resolveCellTarget(position);
      if (!resolved) return [];
      const targets: Element[] = [resolved.$parentCell];
      if (resolved.$targetCell !== resolved.$parentCell) {
        targets.push(resolved.$targetCell);
      }
      if (resolved.$gridCell && resolved.$gridCell !== resolved.$parentCell) {
        targets.push(resolved.$gridCell);
      }
      return targets;
    },
    measureFrame: (position) => {
      const sheet = host.sheet;
      if (!sheet) return null;

      const resolved = host.cellTargets.resolveCellTarget(position);
      if (!resolved) return null;

      const { $parentCell, $targetCell, $gridCell } = resolved;
      if (!$parentCell.cellId || !$targetCell.cellId) return null;

      // For static children (hidden span cells), $gridCell is the
      // hidden grid cell with the correct column for positioning.
      const $anchorCell = $gridCell ?? $parentCell;

      const parentRect = $anchorCell.getBoundingClientRect();
      const targetRect = $targetCell.getBoundingClientRect();
      const widthPx = Math.max(1, targetRect.width);
      const heightPx = Math.max(1, Math.round(targetRect.height));
      const xOffsetPx = targetRect.left - parentRect.left;
      const stackYOffset = Math.round(targetRect.top - parentRect.top);
      const cellRowId = sheet.ids.resolve(
        RowKey.of(String($parentCell.cellId.row)),
      );
      const isHeader = sheet.joinedTree.isInVisibleHeader(cellRowId);
      const anchorColumn = new ColumnIndex.Key.Resolved.WithSubindex(
        nonnull($anchorCell.cellId, "Sheet: anchor cell has no id").col,
        new Rational(0, 1),
      );

      return {
        rowId: cellRowId,
        anchorColumn,
        xOffsetPx,
        yOffsetPx: stackYOffset + (isHeader ? 0 : 3),
        widthPx,
        heightPx,
      };
    },
    fixedChild: host.fixedChild,
  };
}
